import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getSecureInvoice } from "../../../services/restapiService";
import { useRef } from "react";
import {
    BadgeCheck,
    Calendar,
    Car,
    Check,
    CheckCircle,
    ChevronRight,
    Clock3,
    CreditCard,
    Download,
    FileText,
    Gift,
    Headphones,
    Home,
    IdCard,
    Mail,
    MapPin,
    Phone,
    Receipt,
    Route,
    ShieldCheck,
    Sparkles,
    TicketCheck,
    User,
    Users,
    WalletCards,
} from "lucide-react";

const Thankyou = () => {

    const { journeyId } = useParams();
    const navigate = useNavigate();
    console.log("Thankyou journeyId", journeyId);

    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const hasLoaded = useRef(false);
    const [showComingSoon, setShowComingSoon] = useState(false);

    useEffect(() => {

        if (hasLoaded.current) return;
        hasLoaded.current = true;
        const loadInvoice = async () => {
            try {
                const res = await getSecureInvoice(journeyId);
                if (res.data.success) {
                    setInvoice(res.data.data);
                }
            } catch (err) {
                console.error(err);
                if (err.response?.status === 401) {
                    navigate("/payment-login", { replace: true });
                } else if (err.response?.status === 403) {
                    navigate("/home", { replace: true });
                }

            } finally {
                setLoading(false);
            }
        };

        if (journeyId) {
            loadInvoice();
        }

    }, [journeyId, navigate]);



    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 px-4 py-8 sm:px-6 lg:px-8">
                <div className="container animate-pulse">
                    <div className="mb-8 overflow-hidden rounded-3xl border border-white/70 bg-white/70 p-8 shadow-2xl shadow-teal-900/10 backdrop-blur-xl sm:p-12">
                        <div className="mx-auto mb-6 h-24 w-24 rounded-full bg-teal-100" />
                        <div className="mx-auto mb-4 h-8 max-w-sm rounded-full bg-slate-200" />
                        <div className="mx-auto h-4 max-w-lg rounded-full bg-slate-100" />
                        <div className="mt-8 flex flex-wrap justify-center gap-3">
                            <div className="h-10 w-40 rounded-full bg-slate-100" />
                            <div className="h-10 w-36 rounded-full bg-black/20" />
                            <div className="h-10 w-28 rounded-full bg-black/20" />
                        </div>
                    </div>

                    <div className="grid gap-5 lg:grid-cols-2">
                        {[1, 2, 3, 4].map((item) => (
                            <div key={item} className="rounded-3xl border border-white/70 bg-white/70 p-6 shadow-xl shadow-teal-900/5 backdrop-blur-xl">
                                <div className="mb-6 h-5 w-48 rounded-full bg-slate-200" />
                                <div className="space-y-4">
                                    <div className="h-4 rounded-full bg-slate-100" />
                                    <div className="h-4 w-5/6 rounded-full bg-slate-100" />
                                    <div className="h-4 w-3/4 rounded-full bg-slate-100" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (!invoice) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-teal-50 via-white to-emerald-50 px-4 py-12">
                <div className="w-full max-w-xl rounded-3xl border border-white/80 bg-white/80 p-8 text-center shadow-2xl shadow-teal-900/10 backdrop-blur-xl">
                    <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-rose-50 text-rose-500">
                        <Receipt className="h-8 w-8" />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-950">Invoice not found.</h1>
                    <p className="mt-3 text-sm leading-6 text-slate-500">
                        We could not locate the confirmation details for this booking. Please check your trips or return home.
                    </p>
                    <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
                        <button
                            onClick={() => navigate("/my-trips")}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-teal-700 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-teal-700/20 transition hover:-translate-y-0.5 hover:bg-teal-800"
                        >
                            <TicketCheck className="h-4 w-4" />
                            View My Trips
                        </button>
                        <button
                            onClick={() => navigate("/home")}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-teal-700/20 bg-white px-5 py-3 text-sm font-semibold text-teal-800 transition hover:-translate-y-0.5 hover:border-teal-700/40 hover:bg-teal-50"
                        >
                            <Home className="h-4 w-4" />
                            Back to Home
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const detailItemClass = "rounded-2xl border border-slate-200/70 bg-white/70 p-4 shadow-sm shadow-slate-900/5";
    const labelClass = "text-xs font-semibold uppercase tracking-wider text-slate-400";
    const valueClass = "mt-1 break-words text-sm font-semibold text-slate-900";

    const travellerStats = [
        { label: "Adults", value: invoice.adults, icon: User },
        { label: "Children", value: invoice.children, icon: Users },
        { label: "Infants", value: invoice.infants, icon: ShieldCheck },
        { label: "Total Guests", value: invoice.total_guests, icon: BadgeCheck },
    ];

    const timeline = [
        { title: "Booking Created", value: invoice.created_at, icon: Calendar },
        { title: "OTP Verified", value: "Security check completed", icon: ShieldCheck },
        { title: "Payment Successful", value: invoice.payment_status, icon: CreditCard },
        { title: "Invoice Generated", value: invoice.invoice_no, icon: Receipt },
        { title: "Booking Confirmed", value: invoice.tour_title, icon: CheckCircle },
    ];

    return (
        <div className="min-h-screen overflow-hidden bg-gray-200/10 text-slate-950">
            <section className="relative isolate overflow-hidden px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
                <div className="absolute inset-0 -z-10 " />
                <div className="absolute left-10 top-12 -z-10 h-2 w-2 rounded-full bg-amber-300 shadow-[120px_40px_0_rgba(15,118,110,0.28),240px_12px_0_rgba(16,185,129,0.28),480px_70px_0_rgba(251,191,36,0.38),760px_20px_0_rgba(15,118,110,0.22),940px_84px_0_rgba(16,185,129,0.24)]" />
                <div className="absolute bottom-12 right-16 -z-10 h-1.5 w-1.5 rounded-full bg-teal-400 shadow-[-160px_-36px_0_rgba(251,191,36,0.34),-320px_12px_0_rgba(15,118,110,0.24),-620px_-18px_0_rgba(16,185,129,0.3),-900px_30px_0_rgba(251,191,36,0.26)]" />

                <div className="container">
                    <div className="rounded-3xl border border-white/70 bg-white/75 p-6 text-center shadow-2xl shadow-teal-900/10 backdrop-blur-xl sm:p-10 lg:p-12">
                        <div className="mx-auto mb-7 flex w-fit items-center justify-center">
                            <div className="relative">
                                <div className="absolute inset-0 rounded-full bg-black/20 blur-xl" />
                                <div className="absolute inset-0 rounded-full bg-black/10 animate-ping" />
                                <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-black text-white shadow-2xl shadow-emerald-500/30 sm:h-28 sm:w-28">
                                    <Check className="h-12 w-12 sm:h-14 sm:w-14" strokeWidth={3} />
                                </div>
                                <div className="absolute -right-2 -top-2 flex h-9 w-9 items-center justify-center rounded-full bg-amber-300 text-black shadow-lg ">
                                    <Sparkles className="h-4 w-4" />
                                </div>
                            </div>
                        </div>

                        <p className="mb-3 text-sm font-bold uppercase tracking-[0.28em] text-black">GoSehari Luxury Travel</p>
                        <h1 className="text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
                            Payment Successful
                        </h1>
                        <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
                            Thank you for choosing GoSehari. Your booking is confirmed, your invoice is ready, and your journey is now beautifully set in motion.
                        </p>

                        <div className="mt-8 flex flex-wrap justify-center gap-3">
                            <div className="inline-flex items-center gap-2 rounded-full border border-teal-700/15 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm">
                                <Receipt className="h-4 w-4 text-teal-700" />
                                Invoice {invoice.invoice_no}
                            </div>
                            <div className="inline-flex items-center gap-2 rounded-full bg-teal-700 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-teal-700/20">
                                <BadgeCheck className="h-4 w-4" />
                                Booking Confirmed
                            </div>
                            <div className="inline-flex items-center gap-2 rounded-full bg-black/20 px-4 py-2 text-sm font-semibold text-black">
                                <CreditCard className="h-4 w-4" />
                                Paid
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <main className="container space-y-6 px-4 pb-12 sm:px-6 lg:px-8">
                <div className="grid gap-6 lg:grid-cols-2">
                    <section className="rounded-3xl border border-white/80 bg-white/75 p-6 shadow-xl shadow-teal-900/5 backdrop-blur-xl">
                        <div className="mb-6 flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-teal-700 text-white shadow-lg shadow-teal-700/20">
                                <User className="h-5 w-5" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-slate-950">Customer Details</h2>
                                <p className="text-sm text-slate-500">Primary guest and contact information</p>
                            </div>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className={detailItemClass}>
                                <p className={labelClass}>Customer Name</p>
                                <p className={valueClass}>{invoice.customer_name}</p>
                            </div>
                            <div className={detailItemClass}>
                                <p className={labelClass}>Phone</p>
                                <p className={valueClass}>{invoice.customer_phone}</p>
                            </div>
                            <div className={`${detailItemClass} sm:col-span-2`}>
                                <p className={labelClass}>Email</p>
                                <p className={valueClass}>{invoice.customer_email}</p>
                            </div>
                        </div>
                    </section>

                    <section className="rounded-3xl border border-white/80 bg-white/75 p-6 shadow-xl shadow-teal-900/5 backdrop-blur-xl">
                        <div className="mb-6 flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-950 text-white shadow-lg shadow-slate-950/20">
                                <MapPin className="h-5 w-5" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-slate-950">Booking Details</h2>
                                <p className="text-sm text-slate-500">Your selected travel experience</p>
                            </div>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className={detailItemClass}>
                                <p className={labelClass}>Tour</p>
                                <p className={valueClass}>{invoice.tour_title}</p>
                            </div>
                            <div className={detailItemClass}>
                                <p className={labelClass}>Package</p>
                                <p className={valueClass}>{invoice.package_name}</p>
                            </div>
                            <div className={detailItemClass}>
                                <p className={labelClass}>Destination</p>
                                <p className={valueClass}>{invoice.location}</p>
                            </div>
                            <div className={detailItemClass}>
                                <p className={labelClass}>Travel Date</p>
                                <p className={valueClass}>{invoice.travel_date}</p>
                            </div>
                            <div className={detailItemClass}>
                                <p className={labelClass}>Booked On</p>
                                <p className={valueClass}>{invoice.created_at}</p>
                            </div>
                            <div className={detailItemClass}>
                                <p className={labelClass}>Travel Time</p>
                                <p className={valueClass}>{invoice.invoice_no}</p>
                            </div>
                        </div>
                    </section>
                </div>


                <section className="rounded-3xl border border-white/80 bg-white/80 p-5 shadow-xl shadow-teal-900/5 backdrop-blur-xl sm:p-6">
                    <div className="mb-6 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-black/20 text-black">
                                <Users className="h-5 w-5" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-slate-950">Traveller Details</h2>
                                <p className="text-sm text-slate-500">Guest manifest for this booking</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2 pb-4 lg:grid-cols-4">

                        {travellerStats.map((stat) => {
                            const Icon = stat.icon;

                            return (
                                <div key={stat.label} className="group rounded-3xl border border-white/80 bg-white/80 px-4 py-2 shadow-xl shadow-teal-900/5 backdrop-blur-xl transition hover:-translate-y-1 hover:shadow-2xl hover:shadow-teal-900/10">
                                    <div className="flex items-center justify-between gap-4">
                                        <div>
                                            <p className="text-sm font-semibold text-slate-500">{stat.label}</p>
                                            <p className="mt-2 text-lg font-bold text-slate-950">{stat.value}</p>
                                        </div>
                                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-700 transition group-hover:bg-teal-700 group-hover:text-white">
                                            <Icon className="h-6 w-6" />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>


                    {invoice.travellers?.length > 0 ? (
                        <>
                            <div className="hidden overflow-hidden rounded-3xl border border-slate-200/80 md:block">
                                <table className="w-full border-collapse bg-white/80 text-sm">
                                    <thead className="bg-slate-950 text-white">
                                        <tr>
                                            <th className="px-5 py-4 text-left font-semibold">#</th>
                                            <th className="px-5 py-4 text-left font-semibold">Traveller Name</th>
                                            <th className="px-5 py-4 text-left font-semibold">Email</th>
                                            <th className="px-5 py-4 text-left font-semibold">Phone</th>
                                            <th className="px-5 py-4 text-left font-semibold">Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {invoice.travellers.map((traveller, index) => (
                                            <tr key={index} className="transition hover:bg-emerald-50/70">
                                                <td className="px-5 py-4 font-bold text-teal-700">{index + 1}</td>
                                                <td className="px-5 py-4 font-semibold text-slate-950">{traveller.name}</td>
                                                <td className="px-5 py-4 text-slate-600">{traveller.email}</td>
                                                <td className="px-5 py-4 text-slate-600">{traveller.phone}</td>
                                                <td className="px-5 py-4 text-slate-600">{traveller.message || "No remarks"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="grid gap-4 md:hidden">
                                {invoice.travellers.map((traveller, index) => (
                                    <div key={index} className="rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm">
                                        <div className="mb-4 flex items-center justify-between">
                                            <div>
                                                <p className="text-xs font-bold uppercase tracking-wider text-teal-700">Traveller {index + 1}</p>
                                                <h3 className="mt-1 text-lg font-bold text-slate-950">{traveller.name}</h3>
                                            </div>
                                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-50 text-teal-700">
                                                <User className="h-5 w-5" />
                                            </div>
                                        </div>
                                        <div className="space-y-3 text-sm">
                                            <p className="break-words text-slate-600"><span className="font-semibold text-slate-950">Email:</span> {traveller.email}</p>
                                            <p className="text-slate-600"><span className="font-semibold text-slate-950">Phone:</span> {traveller.phone}</p>
                                            <p className="break-words text-slate-600"><span className="font-semibold text-slate-950">Remarks:</span> {traveller.message || "No remarks"}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="rounded-3xl border border-dashed border-slate-300 bg-white/60 p-8 text-center text-sm text-slate-500">
                            Traveller details will appear here once available.
                        </div>
                    )}
                </section>

                <section className="rounded-3xl border border-white/80 bg-white/80 p-5 shadow-xl shadow-teal-900/5 backdrop-blur-xl sm:p-6">
                    <div className="mb-6 flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-teal-700 text-white">
                            <Car className="h-5 w-5" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-950">Vehicle Details</h2>
                            <p className="text-sm text-slate-500">Comfort transport arranged for your route</p>
                        </div>
                    </div>

                    {invoice.vehicles?.length > 0 ? (
                        <div className="">
                            {invoice.vehicles.map((vehicle, index) => (
                                <div key={`${vehicle.vehicle_name}-${index}`} className="rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-xl hover:shadow-teal-900/10">
                                    <div className="mb-5 flex items-start justify-between gap-4">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-950 text-white">
                                                <Car className="h-6 w-6" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-slate-950">{vehicle.vehicle_name}</h3>
                                                <p className="text-sm text-slate-500">{vehicle.vehicle_type}</p>
                                            </div>
                                        </div>
                                        <div className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-bold text-black">
                                            ₹{vehicle.vehicle_price}
                                        </div>
                                    </div>
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        <div className="rounded-2xl bg-slate-50 p-4">
                                            <p className={labelClass}>Pax Capacity</p>
                                            <p className={valueClass}>{vehicle.pax_capacity}</p>
                                        </div>
                                        <div className="rounded-2xl bg-slate-50 p-4">
                                            <p className={labelClass}>Vehicle Price</p>
                                            <p className={valueClass}>₹{vehicle.vehicle_price}</p>
                                        </div>
                                        <div className="rounded-2xl bg-slate-50 p-4 sm:col-span-2">
                                            <div className="mb-2 flex items-center gap-2 text-teal-700">
                                                <Route className="h-4 w-4" />
                                                <p className={labelClass}>Route</p>
                                            </div>
                                            <p className="break-words text-sm font-semibold text-slate-900">{vehicle.route}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="rounded-3xl border border-dashed border-slate-300 bg-white/60 p-8 text-center text-sm text-slate-500">
                            Vehicle details are not attached to this booking.
                        </div>
                    )}



                </section>

                <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
                    <section className="rounded-3xl border border-white/80 bg-white/85 p-5 shadow-xl shadow-teal-900/5 backdrop-blur-xl sm:p-6">
                        <div className="mb-6 flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-950 text-white">
                                <WalletCards className="h-5 w-5" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-slate-950">Payment Summary</h2>
                                <p className="text-sm text-slate-500">Invoice breakdown and total paid</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between gap-4 rounded-2xl bg-slate-50 px-4 py-3">
                                <span className="text-sm font-medium text-slate-600">Activity Price Per Person</span>
                                <span className="text-sm font-bold text-slate-950">₹{invoice.activity_price_per_person}</span>
                            </div>
                            <div className="flex items-center justify-between gap-4 rounded-2xl bg-slate-50 px-4 py-3">
                                <span className="text-sm font-medium text-slate-600">Activity Total</span>
                                <span className="text-sm font-bold text-slate-950">₹{invoice.activity_price}</span>
                            </div>
                            <div className="flex items-center justify-between gap-4 rounded-2xl bg-slate-50 px-4 py-3">
                                <span className="text-sm font-medium text-slate-600">Vehicle Charges</span>
                                <span className="text-sm font-bold text-slate-950">₹{invoice.vehicle_price}</span>
                            </div>
                            <div className="flex items-center justify-between gap-4 rounded-2xl bg-slate-50 px-4 py-3">
                                <span className="text-sm font-medium text-slate-600">GST</span>
                                <span className="text-sm font-bold text-slate-950">₹{invoice.gst_amount}</span>
                            </div>
                            <div className="flex items-center justify-between gap-4 rounded-2xl bg-emerald-50 px-4 py-3">
                                <span className="text-sm font-medium text-black">Discount</span>
                                <span className="text-sm font-bold text-emerald-900">₹0</span>
                            </div>
                            <div className="flex items-center justify-between gap-4 rounded-2xl bg-amber-50 px-4 py-3">
                                <span className="text-sm font-medium text-amber-800">Coupon</span>
                                <span className="text-sm font-bold text-amber-900">Not applied</span>
                            </div>

                            <div className="mt-5 rounded-3xl bg-black p-3 text-white shadow-2xl shadow-teal-700/20">
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <p className="text-sm font-semibold uppercase tracking-wider text-white">Grand Total</p>
                                        <p className="mt-1 text-sm text-white/75">Amount paid successfully</p>
                                    </div>
                                    <p className="text-xl sm:text-2xl md:text-3xl lg:text-3xl font-bold text-white">
                                        ₹{invoice.total_amount}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="rounded-3xl border border-white/80 bg-slate-950 p-5 text-white shadow-xl shadow-slate-950/15 sm:p-6">
                        <div className="mb-6 flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-emerald-300">
                                <CreditCard className="h-5 w-5" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold">Payment Information</h2>
                                <p className="text-sm text-white/55">Secure transaction reference</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                                <span className="text-sm text-white">Status</span>
                                <span className="inline-flex items-center gap-2 rounded-full bg-emerald-400/15 px-3 py-1 text-sm font-bold text-emerald-200">
                                    <CheckCircle className="h-4 w-4" />
                                    {invoice.payment_status}
                                </span>
                            </div>
                            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                <p className="text-xs font-semibold uppercase tracking-wider text-white">Payment Method</p>
                                <p className="mt-2 break-words text-sm font-semibold text-white">{invoice.payment_method}</p>
                            </div>
                            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                <p className="text-xs font-semibold uppercase tracking-wider text-white">Order ID</p>
                                <p className="mt-2 break-words text-sm font-semibold text-white">{invoice.razorpay_order_id}</p>
                            </div>
                            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                <p className="text-xs font-semibold uppercase tracking-wider text-white">Payment ID</p>
                                <p className="mt-2 break-words text-sm font-semibold text-white">{invoice.razorpay_payment_id}</p>
                            </div>
                        </div>
                    </section>
                </div>


                <section className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
                    <div className="rounded-3xl border border-white/80 bg-white/80 p-6 shadow-xl shadow-teal-900/5 backdrop-blur-xl">
                        <div className="mb-6 flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                                <Gift className="h-5 w-5" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-slate-950">What's Next</h2>
                                <p className="text-sm text-slate-500">A few notes before you travel</p>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {[
                                ["Confirmation email sent", Mail],
                                ["Carry valid ID", IdCard],
                                ["Arrive 15 minutes early", Clock3],
                                ["Support available", Headphones],
                            ].map(([text, Icon]) => (
                                <div key={text} className="flex items-center gap-3 rounded-2xl bg-slate-50 p-4">
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-teal-700 shadow-sm">
                                        <Icon className="h-4 w-4" />
                                    </div>
                                    <p className="text-sm font-semibold text-slate-700">{text}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-3xl border border-white/80 bg-gradient-to-br from-white to-emerald-50 p-6 shadow-xl shadow-teal-900/5">
                        <div className="mb-6 flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-teal-700 text-white">
                                <FileText className="h-5 w-5" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-slate-950">Manage Booking</h2>
                                <p className="text-sm text-slate-500">Save your invoice or review your upcoming trips</p>
                            </div>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-3">
                            <button
                                onClick={() => window.print()}
                                className="inline-flex min-h-14 items-center justify-center gap-2 rounded-2xl bg-teal-700 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-teal-700/20 transition hover:-translate-y-0.5 hover:bg-teal-800"
                            >
                                <Download className="h-4 w-4" />
                                Download Invoice PDF
                            </button>
                            <button
                                onClick={() => setShowComingSoon(true)}
                                className="inline-flex min-h-14 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/15 transition hover:-translate-y-0.5 hover:bg-slate-800"
                            >
                                <TicketCheck className="h-4 w-4" />
                                View My Trips
                            </button>
                            <button
                                onClick={() => navigate("/home")}
                                className="inline-flex min-h-14 items-center justify-center gap-2 rounded-2xl border border-teal-700/20 bg-white px-4 py-3 text-sm font-bold text-teal-800 transition hover:-translate-y-0.5 hover:border-teal-700/40 hover:bg-teal-50"
                            >
                                <Home className="h-4 w-4" />
                                Back to Home
                            </button>
                        </div>
                    </div>
                </section>
            </main>



            {showComingSoon && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-md px-4 animate-in fade-in duration-300">
                    <div className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl animate-in zoom-in-95 duration-300">

                        {/* Decorative Top */}
                        <div className="h-2 bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500" />

                        <div className="p-8 text-center">

                            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-amber-100 to-orange-100 shadow-lg">
                                <Sparkles className="h-10 w-10 text-amber-600" />
                            </div>

                            <h2 className="text-2xl font-bold text-slate-900">
                                Coming Soon
                            </h2>

                            <p className="mt-3 text-sm leading-6 text-slate-500">
                                <strong>My Trips</strong> is currently under development.
                                <br />
                                You'll soon be able to view all your bookings, track trip
                                status, and download invoices from one place.
                            </p>

                            <div className="mt-6 flex items-center justify-center gap-2 rounded-2xl bg-amber-50 px-4 py-3 text-amber-700">
                                <Clock3 className="h-5 w-5" />
                                <span className="text-sm font-semibold">
                                    Stay tuned! We're launching this feature soon.
                                </span>
                            </div>

                            <button
                                onClick={() => setShowComingSoon(false)}
                                className="mt-8 w-full rounded-2xl bg-slate-900 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                            >
                                Got it
                            </button>

                        </div>
                    </div>
                </div>
            )}


            <footer className="border-t border-slate-200 bg-white px-4 py-8 sm:px-6 lg:px-8">
                <div className="container">

                    <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">

                        {/* Left */}
                        <div className="text-center lg:text-left">
                            <h3 className="text-lg font-bold text-slate-900">
                                Need Support?
                            </h3>

                            <p className="mt-1 text-sm text-slate-500">
                                Our support team is available to assist you with your booking.
                            </p>
                        </div>

                        {/* Center */}
                        <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-6">

                            <a
                                href="mailto:support@gosehari.com"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-teal-900 transition"
                            >
                                <Mail className="h-4 w-4" />
                                support@gosehari.com
                            </a>

                            <a
                                href="tel:+919620267111"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-teal-900 transition"
                            >
                                <Phone className="h-4 w-4" />
                                +91 96202 67111
                            </a>

                        </div>


                    </div>

                </div>
            </footer>
        </div>
    );
};

export default Thankyou;
